import React, { useState, useEffect, useRef, useCallback } from 'react';
import FroalaEditor from 'froala-editor';
import FroalaEditorComponent from 'react-froala-wysiwyg';
import { createFroalaConfig, contractPasteConfig } from '../../../../config/froalaConfig';
import { X, Search, Eye, EyeOff, FileText, RefreshCw, Loader2 } from 'lucide-react';

// Import Froala plugins
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/font_family.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/fullscreen.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/line_height.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/table.min.js';

// Import Froala CSS
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';

// Register custom commands with Froala (only once)
let contractCommandsRegistered = false;

const registerContractCommands = () => {
    if (contractCommandsRegistered || !FroalaEditor) return;

    // Variables button
    FroalaEditor.DefineIcon('contractVariables', {
        NAME: 'contractVariables',
        template: 'text'
    });

    FroalaEditor.RegisterCommand('contractVariables', {
        title: 'Variablen einfügen',
        icon: 'Variablen',
        text: 'Variablen',
        undo: false,
        focus: false,
        showOnMobile: true,
        refreshAfterCallback: false,
        callback: function() {
            this.selection.save();
            window._froalaContractEditor = this;
            window.dispatchEvent(new CustomEvent('open-contract-variables-modal'));
        }
    });

    // Page break button
    FroalaEditor.DefineIcon('pageBreak', {
        NAME: 'pageBreak',
        SVG_KEY: 'horizontalLine'
    });

    FroalaEditor.RegisterCommand('pageBreak', {
        title: 'Seitenumbruch einfügen',
        icon: 'pageBreak',
        undo: true,
        focus: true,
        showOnMobile: true,
        refreshAfterCallback: true,
        callback: function() {
            // Insert visual page break marker
            this.html.insert('<div style="border-top: 2px dashed #cbd5e1; margin: 1.5rem 0; padding-top: 0.5rem; text-align: center; color: #94a3b8; font-size: 0.75rem;">── Seitenumbruch ──</div>{{NeueSeite}}');
        }
    });

    contractCommandsRegistered = true;
};

// Register on module load
registerContractCommands();

/**
 * Get variable type for contract type
 * @param {number} contractType - 1 for Jobs, 2 for Weddings
 * @returns {string} - Variable type ID
 */
const getVariableTypeForContractType = (contractType) => {
    const type = parseInt(contractType);
    if (type === 1) return '1'; // Jobs
    if (type === 2) return '2'; // Weddings
    return '1'; // Default to Jobs
};

const ContentTab = ({ contract, onChange }) => {
    const [variables, setVariables] = useState([]);
    const [showVariablesModal, setShowVariablesModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoadingVariables, setIsLoadingVariables] = useState(false);
    const [editorReady, setEditorReady] = useState(false);
    const [showPreview, setShowPreview] = useState(true);
    const [previewMode, setPreviewMode] = useState('pdf'); // 'pdf' or 'html'
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isLoadingPdf, setIsLoadingPdf] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const mountedRef = useRef(true);
    const debounceTimerRef = useRef(null);
    const lastContentRef = useRef('');

    // Register command and delay editor mount
    useEffect(() => {
        registerContractCommands();
        mountedRef.current = true;

        const timer = setTimeout(() => {
            if (mountedRef.current) {
                setEditorReady(true);
            }
        }, 100);

        return () => {
            mountedRef.current = false;
            setEditorReady(false);
            if (window._froalaContractEditor) {
                window._froalaContractEditor = null;
            }
            // Clean up PDF blob URL
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
            // Clear debounce timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Listen for open modal event
    useEffect(() => {
        const handleOpenModal = () => {
            setShowVariablesModal(true);
        };

        window.addEventListener('open-contract-variables-modal', handleOpenModal);
        return () => {
            window.removeEventListener('open-contract-variables-modal', handleOpenModal);
        };
    }, []);

    // Fetch variables when contract type changes
    useEffect(() => {
        // Always fetch variables, default to type 1 (Jobs) if not set
        const typeToFetch = contract?.type || 1;
        fetchVariables(typeToFetch);
    }, [contract?.type]);

    // Generate PDF preview with debouncing
    const generatePdfPreview = useCallback(async (content, type) => {
        if (!content || content.trim() === '') {
            setPdfUrl(null);
            setPdfError(null);
            return;
        }

        setIsLoadingPdf(true);
        setPdfError(null);

        try {
            const response = await fetch('/index.php/contracts/previewPDF', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    content: content,
                    type: type || 1
                })
            });

            if (!response.ok) {
                throw new Error('PDF-Generierung fehlgeschlagen');
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                // Error response
                const errorData = await response.json();
                throw new Error(errorData.error || 'Unbekannter Fehler');
            }

            // Get PDF blob
            const blob = await response.blob();

            // Clean up old URL
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }

            // Create new blob URL
            const newUrl = URL.createObjectURL(blob);
            if (mountedRef.current) {
                setPdfUrl(newUrl);
            }
        } catch (error) {
            console.error('PDF preview error:', error);
            if (mountedRef.current) {
                setPdfError(error.message);
            }
        } finally {
            if (mountedRef.current) {
                setIsLoadingPdf(false);
            }
        }
    }, [pdfUrl]);

    // Debounced PDF preview update
    useEffect(() => {
        if (previewMode !== 'pdf' || !showPreview) return;

        // Only update if content actually changed
        if (contract?.content === lastContentRef.current) return;
        lastContentRef.current = contract?.content || '';

        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set loading state immediately for feedback
        if (contract?.content && contract.content.trim() !== '') {
            setIsLoadingPdf(true);
        }

        // Debounce: wait 1000ms after last change before generating PDF
        debounceTimerRef.current = setTimeout(() => {
            generatePdfPreview(contract?.content, contract?.type);
        }, 1000);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [contract?.content, contract?.type, previewMode, showPreview, generatePdfPreview]);

    // Manual refresh
    const handleManualRefresh = () => {
        generatePdfPreview(contract?.content, contract?.type);
    };

    // Fetch variables from backend
    const fetchVariables = async (contractType) => {
        setIsLoadingVariables(true);
        try {
            const varType = getVariableTypeForContractType(contractType);
            const response = await fetch('/index.php/variables/getVariablesForContracts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: `type=${varType}`
            });
            if (response.ok) {
                const data = await response.json();
                const processedVariables = Array.isArray(data) ? data.map(item => ({
                    id: item.variablen_id || item[0],
                    name: item.variablen_name || item[1] || 'Unbenannt',
                    variable_id: item.variablen_id || item[0]
                })) : [];
                setVariables(processedVariables);
            }
        } catch (error) {
            console.error('Error fetching variables:', error);
            setVariables([]);
        } finally {
            setIsLoadingVariables(false);
        }
    };

    // Insert variable content
    const insertVariable = async (variableId) => {
        try {
            const formData = new FormData();
            formData.append('varID', variableId);
            formData.append('art', 'template');

            const response = await fetch('/index.php/variables/variables', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const content = await response.text();

                if (window._froalaContractEditor) {
                    try {
                        window._froalaContractEditor.selection.restore();
                        window._froalaContractEditor.html.insert(content);
                    } catch (e) {
                        onChange({ content: (contract?.content || '') + content });
                    }
                } else {
                    onChange({ content: (contract?.content || '') + content });
                }
            }
        } catch (error) {
            console.error('Error inserting variable:', error);
        }

        setShowVariablesModal(false);
        setSearchTerm('');
    };

    // Handle content change
    const handleContentChange = (newContent) => {
        onChange({ content: newContent });
    };

    // Filter variables
    const filteredVariables = variables.filter(v =>
        v.name && v.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Froala config
    const froalaConfig = {
        ...createFroalaConfig({
            height: 500,
            placeholderText: 'Vertragsinhalt eingeben...'
        }),
        ...contractPasteConfig,
        toolbarButtons: [
            'paragraphFormat', 'fontSize', '|',
            'bold', 'italic', 'underline', 'clearFormatting', '|',
            'fontFamily', 'textColor', 'backgroundColor', '|',
            'formatUL', 'formatOL', 'lineHeight', 'align', '|',
            'contractVariables', 'pageBreak', '|',
            'insertTable', 'insertLink', '|',
            'html', 'fullscreen'
        ]
    };

    // Modal styles
    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
    };

    const modalStyle = {
        background: 'var(--bg-card, #ffffff)',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    };

    const modalHeaderStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.25rem',
        borderBottom: '1px solid var(--border-light, #e2e8f0)'
    };

    const modalBodyStyle = {
        padding: '1rem 1.25rem',
        overflowY: 'auto',
        flex: 1
    };

    const searchInputStyle = {
        width: '100%',
        padding: '0.625rem 1rem 0.625rem 2.5rem',
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        fontSize: '0.875rem',
        background: 'var(--bg-card, #ffffff)',
        color: 'var(--text-primary, #2c3e50)',
        marginBottom: '1rem'
    };

    const variableItemStyle = {
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        borderBottom: '1px solid var(--border-light, #e2e8f0)'
    };

    const contractType = parseInt(contract?.type) || 0;
    const contractTypeLabel = contractType === 1 ? 'Jobs' : contractType === 2 ? 'Hochzeiten' : 'Allgemein';

    // Preview toggle button style
    const previewToggleStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        background: showPreview ? 'var(--accent-slate, #5b7c8d)' : 'var(--bg-card, #ffffff)',
        color: showPreview ? 'white' : 'var(--text-secondary, #64748b)',
        fontSize: '0.8125rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    // Preview mode toggle style
    const previewModeButtonStyle = (isActive) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.375rem',
        padding: '0.375rem 0.75rem',
        border: 'none',
        borderRadius: '6px',
        background: isActive ? 'var(--accent-slate, #5b7c8d)' : 'transparent',
        color: isActive ? 'white' : 'var(--text-secondary, #64748b)',
        fontSize: '0.75rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    });

    // Preview container style
    const previewContainerStyle = {
        flex: 1,
        border: '1px solid var(--border-light, #e2e8f0)',
        borderRadius: '8px',
        background: 'var(--bg-card, #ffffff)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '500px'
    };

    const previewHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.5rem 1rem',
        background: 'var(--bg-secondary, #f5f7f9)',
        borderBottom: '1px solid var(--border-light, #e2e8f0)',
        fontSize: '0.8125rem',
        fontWeight: '600',
        color: 'var(--text-secondary, #64748b)'
    };

    const previewContentStyle = {
        flex: 1,
        padding: previewMode === 'html' ? '1.5rem' : 0,
        overflowY: 'auto',
        fontSize: '0.9375rem',
        lineHeight: '1.6',
        color: 'var(--text-primary, #2c3e50)'
    };

    return (
        <div className="contract-content-tab">
            {/* Header with Info and Preview Toggle */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                flexWrap: 'wrap',
                gap: '0.75rem'
            }}>
                <div style={{
                    padding: '0.75rem 1rem',
                    background: 'rgba(91, 124, 141, 0.08)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary, #64748b)',
                    flex: 1
                }}>
                    <strong style={{ color: 'var(--text-primary, #2c3e50)' }}>Tipp:</strong>{' '}
                    Klicken Sie auf "Variablen" in der Toolbar, um dynamische Platzhalter einzufügen.
                    Aktuell verfügbar: <strong>{contractTypeLabel}</strong>-Variablen
                </div>
                <button
                    type="button"
                    style={previewToggleStyle}
                    onClick={() => setShowPreview(!showPreview)}
                >
                    {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                    {showPreview ? 'Vorschau ausblenden' : 'Vorschau anzeigen'}
                </button>
            </div>

            {/* Split View: Editor + Preview */}
            <div style={{
                display: 'flex',
                gap: '1.5rem',
                alignItems: 'stretch'
            }}>
                {/* Editor Column */}
                <div style={{ flex: showPreview ? '1 1 50%' : '1 1 100%', minWidth: 0 }}>
                    {editorReady ? (
                        <FroalaEditorComponent
                            tag="textarea"
                            config={froalaConfig}
                            model={contract?.content || ''}
                            onModelChange={handleContentChange}
                        />
                    ) : (
                        <div style={{
                            height: 500,
                            border: '1px solid var(--border-medium, #cbd5e1)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'var(--bg-card, #ffffff)',
                            color: 'var(--text-secondary, #64748b)'
                        }}>
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            Editor wird geladen...
                        </div>
                    )}
                </div>

                {/* Preview Column */}
                {showPreview && (
                    <div style={{ flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                        <div style={previewContainerStyle}>
                            {/* Preview Header with Mode Toggle */}
                            <div style={previewHeaderStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <FileText size={16} />
                                    <span>Vorschau</span>
                                    {isLoadingPdf && previewMode === 'pdf' && (
                                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {/* Mode Toggle */}
                                    <div style={{
                                        display: 'flex',
                                        background: 'var(--bg-card, #ffffff)',
                                        borderRadius: '6px',
                                        padding: '2px',
                                        border: '1px solid var(--border-light, #e2e8f0)'
                                    }}>
                                        <button
                                            type="button"
                                            style={previewModeButtonStyle(previewMode === 'pdf')}
                                            onClick={() => setPreviewMode('pdf')}
                                            title="PDF-Vorschau (zeigt das echte PDF)"
                                        >
                                            <FileText size={12} />
                                            PDF
                                        </button>
                                        <button
                                            type="button"
                                            style={previewModeButtonStyle(previewMode === 'html')}
                                            onClick={() => setPreviewMode('html')}
                                            title="HTML-Vorschau (schnelle Vorschau)"
                                        >
                                            <Eye size={12} />
                                            HTML
                                        </button>
                                    </div>
                                    {/* Refresh Button (PDF only) */}
                                    {previewMode === 'pdf' && (
                                        <button
                                            type="button"
                                            onClick={handleManualRefresh}
                                            disabled={isLoadingPdf}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '28px',
                                                height: '28px',
                                                border: '1px solid var(--border-light, #e2e8f0)',
                                                borderRadius: '6px',
                                                background: 'var(--bg-card, #ffffff)',
                                                color: 'var(--text-secondary, #64748b)',
                                                cursor: isLoadingPdf ? 'not-allowed' : 'pointer',
                                                opacity: isLoadingPdf ? 0.5 : 1
                                            }}
                                            title="Vorschau aktualisieren"
                                        >
                                            <RefreshCw size={14} style={{ animation: isLoadingPdf ? 'spin 1s linear infinite' : 'none' }} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Preview Content */}
                            <div style={previewContentStyle}>
                                {previewMode === 'pdf' ? (
                                    // PDF Preview
                                    <>
                                        {pdfError ? (
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                                color: 'var(--text-secondary, #64748b)',
                                                textAlign: 'center',
                                                padding: '2rem'
                                            }}>
                                                <FileText size={48} style={{ opacity: 0.3, marginBottom: '1rem', color: '#ef4444' }} />
                                                <p style={{ margin: 0, color: '#ef4444', fontWeight: 500 }}>Fehler bei der PDF-Generierung</p>
                                                <p style={{ margin: '0.5rem 0 1rem', fontSize: '0.8125rem' }}>{pdfError}</p>
                                                <button
                                                    type="button"
                                                    onClick={handleManualRefresh}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        padding: '0.5rem 1rem',
                                                        border: '1px solid var(--border-medium, #cbd5e1)',
                                                        borderRadius: '6px',
                                                        background: 'var(--bg-card, #ffffff)',
                                                        color: 'var(--text-primary, #2c3e50)',
                                                        fontSize: '0.8125rem',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <RefreshCw size={14} />
                                                    Erneut versuchen
                                                </button>
                                            </div>
                                        ) : pdfUrl ? (
                                            <iframe
                                                src={pdfUrl}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    border: 'none',
                                                    minHeight: '450px'
                                                }}
                                                title="PDF Vorschau"
                                            />
                                        ) : (
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                                minHeight: '300px',
                                                color: 'var(--text-muted, #94a3b8)',
                                                textAlign: 'center',
                                                padding: '2rem'
                                            }}>
                                                {isLoadingPdf ? (
                                                    <>
                                                        <div className="spinner-border mb-3" role="status" style={{ color: 'var(--accent-slate, #5b7c8d)' }}></div>
                                                        <p style={{ margin: 0 }}>PDF wird generiert...</p>
                                                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
                                                            Variablen werden ersetzt und das Layout wird berechnet
                                                        </p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <FileText size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                                        <p style={{ margin: 0 }}>Beginnen Sie mit der Eingabe</p>
                                                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
                                                            Die PDF-Vorschau wird automatisch generiert
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    // HTML Preview
                                    <>
                                        {contract?.content ? (
                                            <div
                                                className="contract-preview-content"
                                                dangerouslySetInnerHTML={{ __html: contract.content }}
                                            />
                                        ) : (
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                                minHeight: '300px',
                                                color: 'var(--text-muted, #94a3b8)',
                                                textAlign: 'center'
                                            }}>
                                                <Eye size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                                <p style={{ margin: 0 }}>Beginnen Sie mit der Eingabe, um die Vorschau zu sehen</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Variables Modal */}
            {showVariablesModal && (
                <div
                    style={modalOverlayStyle}
                    onClick={() => setShowVariablesModal(false)}
                >
                    <div
                        style={modalStyle}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={modalHeaderStyle}>
                            <h5 style={{
                                margin: 0,
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: 'var(--text-primary, #2c3e50)'
                            }}>
                                Variable auswählen ({contractTypeLabel})
                            </h5>
                            <button
                                type="button"
                                onClick={() => setShowVariablesModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.25rem',
                                    color: 'var(--text-secondary, #64748b)'
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={modalBodyStyle}>
                            {/* Search */}
                            <div style={{ position: 'relative' }}>
                                <Search
                                    size={18}
                                    style={{
                                        position: 'absolute',
                                        left: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'var(--text-muted, #94a3b8)'
                                    }}
                                />
                                <input
                                    type="text"
                                    style={searchInputStyle}
                                    placeholder="Variable suchen..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {/* Variables List */}
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {isLoadingVariables ? (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '2rem',
                                        color: 'var(--text-secondary, #64748b)'
                                    }}>
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        Lade Variablen...
                                    </div>
                                ) : filteredVariables.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '2rem',
                                        color: 'var(--text-secondary, #64748b)'
                                    }}>
                                        {searchTerm ? 'Keine Variablen gefunden' : 'Keine Variablen verfügbar'}
                                    </div>
                                ) : (
                                    filteredVariables.map((variable) => (
                                        <div
                                            key={variable.id}
                                            style={variableItemStyle}
                                            onClick={() => insertVariable(variable.variable_id)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--bg-hover, #f8fafc)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <span style={{
                                                fontWeight: '500',
                                                color: 'var(--text-primary, #2c3e50)'
                                            }}>
                                                {variable.name}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CSS for spin animation */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default ContentTab;
